import {
  Address,
  GetItemsProps,
  ResponseDTO,
  getDate,
  initialStatePhoneDTO,
  mobilePhoneToString,
  toISOString,
  validMobileForInputPhone,
} from 'medere-ui-framework'
import { axiosInstance } from '../../App'
import { ComboDataDTO } from '../../dto/ComboDataDTO'
import { HealthEntityListItemDTO } from '../../dto/HealthEntityListItemDTO'
import { HealthEntitySaveDTO } from '../../dto/HealthEntitySaveDTO'

type UnknownRecord = Record<string, unknown>

interface HealthEntityAddressBackendDTO {
  address: number | null
  addressdesc: string
  telephone: string
  postalcode: string
  medereentity: number | null
  city: number | null
  cityExternalId?: boolean | null
  citydesc: string
  email: string
  types: ComboDataDTO[]
  hasChanges?: boolean
}

interface HealthEntitySaveBackendDTO extends Omit<HealthEntitySaveDTO, 'addresses' | 'iibbFromDate' | 'iibbToDate' | 'companystartdate'> {
  addresses: HealthEntityAddressBackendDTO[]
  companystartdate: string | null
  iibbFromDate: string | null
  iibbToDate: string | null
}

interface ComboLike {
  id?: unknown
  value?: unknown
  key?: unknown
  comboId?: unknown
  identifier?: unknown
  low?: unknown
  intValue?: unknown
  intCompact?: unknown
  _value?: unknown
  name?: unknown
  description?: unknown
  label?: unknown
  recipeValidator?: unknown
  studyOrderRepository?: unknown
  informationProvider?: unknown
  vatcustomercategory?: unknown
  categoryname?: unknown
  paymentMethod?: unknown
  methodName?: unknown
  documentType?: unknown
  shortName?: unknown
  type?: unknown
  phonenumbertype?: unknown
  phoneType?: unknown
  typedesc?: unknown
}

export interface HealthEntityListQueryParams {
  healthEntityStatus?: boolean[]
  typeFilter?: string[]
  multiSortMeta?: unknown
  filterDTO?: unknown
  cardFilterDTO?: unknown
}

const fallbackPhoneTypes: ComboDataDTO[] = [
  { id: 1, name: 'TEL' },
  { id: 2, name: 'CEL' },
]

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim()
}

function parsePositiveNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

function parseComboId(rawId: unknown): number {
  if (rawId == null) return -1

  if (typeof rawId === 'number') {
    return Number.isFinite(rawId) ? rawId : -1
  }

  if (typeof rawId === 'string') {
    const parsed = Number(rawId)
    return Number.isFinite(parsed) ? parsed : -1
  }

  const rawObj = toRecord(rawId)
  if (rawObj) {
    const candidates = [
      rawObj.id,
      rawObj.value,
      rawObj.low,
      rawObj.intValue,
      rawObj.intCompact,
      rawObj._value,
    ]

    for (const candidate of candidates) {
      const parsed = parseComboId(candidate)
      if (parsed > 0) return parsed
    }
  }

  return -1
}

function extractArray(rawData: unknown): unknown[] {
  if (Array.isArray(rawData)) return rawData
  const rawObj = toRecord(rawData)
  if (!rawObj) return []
  if (Array.isArray(rawObj.data)) return rawObj.data
  if (Array.isArray(rawObj.items)) return rawObj.items
  if (Array.isArray(rawObj.content)) return rawObj.content
  return []
}

function normalizeComboItems(rawData: unknown): ComboDataDTO[] {
  return extractArray(rawData)
    .map((item): ComboDataDTO | null => {
      const rawObj = toRecord(item) as ComboLike | null
      if (!rawObj) return null
      const id = parseComboId(rawObj.id ?? rawObj.value ?? rawObj.key ?? rawObj.comboId ?? rawObj.identifier)
      const name = toStringSafe(rawObj.name ?? rawObj.description ?? rawObj.label)
      if (id <= 0 || name === '') return null
      return { id, name }
    })
    .filter((item): item is ComboDataDTO => item !== null)
}

function getComboName(type: unknown): string | null {
  const rawObj = toRecord(type)
  if (!rawObj) return null
  const typed = rawObj as ComboLike
  return toStringSafe(typed.description ?? typed.name) || null
}

function toBackendAddress(address: Address, medereentity?: number | null): HealthEntityAddressBackendDTO {
  const firstPhone = address.phones?.find((phone) => (phone.phonenumber?.number ?? '').trim() !== '')
  return {
    address: address.address > 0 ? address.address : null,
    addressdesc: address.addressdesc ?? '',
    telephone: firstPhone ? mobilePhoneToString(firstPhone.phonenumber) : '',
    postalcode: address.postalcode ?? '',
    medereentity: medereentity ?? null,
    city: address.city > 0 ? address.city : null,
    cityExternalId: (address as any).cityExternalId ?? false,
    citydesc: address.citydesc ?? '',
    email: address.email ?? '',
    types: (address.types ?? []).map((type) => ({
      id: type?.id ?? null,
      name: getComboName(type),
    })),
    hasChanges: true,
  }
}

function hasAnyPhoneNumber(address: Address): boolean {
  return (address.phones ?? []).some((phone) => (phone.phonenumber?.number ?? '').trim() !== '')
}

function isMeaningfulFrontendAddress(address: Address): boolean {
  const hasAddressDesc = (address.addressdesc ?? '').trim() !== ''
  const hasPostalCode = (address.postalcode ?? '').trim() !== ''
  const hasEmail = (address.email ?? '').trim() !== ''
  const hasTypes = (address.types ?? []).length > 0
  const hasPhone = hasAnyPhoneNumber(address)
  return hasAddressDesc || hasPostalCode || hasEmail || hasTypes || hasPhone
}

function isMeaningfulBackendAddress(address: HealthEntityAddressBackendDTO): boolean {
  const hasAddressDesc = (address.addressdesc ?? '').trim() !== ''
  const hasPostalCode = (address.postalcode ?? '').trim() !== ''
  const hasEmail = (address.email ?? '').trim() !== ''
  const hasTypes = (address.types ?? []).length > 0
  const hasPhone = (address.telephone ?? '').trim() !== ''
  return hasAddressDesc || hasPostalCode || hasEmail || hasTypes || hasPhone
}

function toFrontendAddress(address: HealthEntityAddressBackendDTO, medereentity?: number | null): Address {
  const phoneNumber = (address.telephone ?? '').trim() !== ''
    ? validMobileForInputPhone(address.telephone)
    : { ...initialStatePhoneDTO.phonenumber }

  return {
    address: address.address ?? -1,
    addressdesc: address.addressdesc ?? '',
    phones: [{
      ...initialStatePhoneDTO,
      phonenumber: phoneNumber,
      type: 1,
      typedesc: 'Principal',
    }],
    postalcode: address.postalcode ?? '',
    city: address.city ?? -1,
    cityExternalId: address.cityExternalId ?? false,
    citydesc: address.citydesc ?? '',
    email: address.email ?? '',
    types: (address.types ?? []).map((type) => ({
      id: type?.id ?? -1,
      description: toStringSafe(type?.name),
    })),
    medereentity: medereentity ?? -1,
    hasChanges: false,
  } as Address & { cityExternalId?: boolean }
}

function toBackendPayload(healthEntity: HealthEntitySaveDTO): HealthEntitySaveBackendDTO {
  return {
    ...healthEntity,
    companystartdate: toISOString(getDate(healthEntity.companystartdate)) ?? null,
    iibbFromDate: toISOString(healthEntity.iibbFromDate ? new Date(healthEntity.iibbFromDate) : undefined) ?? null,
    iibbToDate: toISOString(healthEntity.iibbToDate ? new Date(healthEntity.iibbToDate) : undefined) ?? null,
    addresses: (healthEntity.addresses ?? [])
      .filter((address) => isMeaningfulFrontendAddress(address))
      .map((address) => toBackendAddress(address, healthEntity.medereentity)),
  }
}

function toFrontendPayload(healthEntity: HealthEntitySaveBackendDTO): HealthEntitySaveDTO {
  return {
    ...healthEntity,
    commercialname: healthEntity.commercialname ?? '',
    businessname: healthEntity.businessname ?? '',
    externalid: healthEntity.externalid ?? '',
    phone: healthEntity.phone ?? '',
    email: healthEntity.email ?? '',
    active: healthEntity.active ?? true,
    classification: healthEntity.classification ?? 1,
    classificationdesc: healthEntity.classificationdesc ?? 'Prestacional',
    cuit: healthEntity.cuit ?? '',
    documenttype: healthEntity.documenttype ?? null,
    documenttypedesc: healthEntity.documenttypedesc ?? '',
    documentnumber: healthEntity.documentnumber ?? '',
    vatcategory: healthEntity.vatcategory ?? null,
    vatcategorydesc: healthEntity.vatcategorydesc ?? '',
    afipstatus: healthEntity.afipstatus ?? null,
    afipstatusdesc: healthEntity.afipstatusdesc ?? '',
    electroniccreditinvoice: healthEntity.electroniccreditinvoice ?? false,
    fiscaladdress: healthEntity.fiscaladdress ?? null,
    fiscaladdressdesc: healthEntity.fiscaladdressdesc ?? '',
    defaultinvoiceissuerentity: healthEntity.defaultinvoiceissuerentity ?? null,
    definvoiceissuername: healthEntity.definvoiceissuername ?? '',
    defaultpos: healthEntity.defaultpos ?? null,
    defaultposname: healthEntity.defaultposname ?? '',
    defaultpaymentmethod: healthEntity.defaultpaymentmethod ?? null,
    defaultpaymentmethodname: healthEntity.defaultpaymentmethodname ?? '',
    defaultbankaccount: healthEntity.defaultbankaccount ?? null,
    defaultbankaccountname: healthEntity.defaultbankaccountname ?? '',
    isThisInstitution: healthEntity.isThisInstitution ?? false,
    afipalias: healthEntity.afipalias ?? '',
    afipproductionmode: healthEntity.afipproductionmode ?? false,
    iibbperceptionagent: healthEntity.iibbperceptionagent ?? false,
    paymentaccounts: healthEntity.paymentaccounts ?? [],
    companystartdate: healthEntity.companystartdate ?? null,
    iibbFromDate: healthEntity.iibbFromDate ?? null,
    iibbToDate: healthEntity.iibbToDate ?? null,
    addresses: (healthEntity.addresses ?? [])
      .filter((address) => isMeaningfulBackendAddress(address))
      .map((address) => toFrontendAddress(address, healthEntity.medereentity)),
  }
}

function toComboDTO(item: unknown, idCandidates: Array<keyof ComboLike>, nameCandidates: Array<keyof ComboLike>): ComboDataDTO | null {
  const rawObj = toRecord(item) as ComboLike | null
  if (!rawObj) return null

  let resolvedId = -1
  for (const key of idCandidates) {
    const parsed = parseComboId(rawObj[key])
    if (parsed > 0) {
      resolvedId = parsed
      break
    }
  }

  let resolvedName = ''
  for (const key of nameCandidates) {
    const parsedName = toStringSafe(rawObj[key])
    if (parsedName !== '') {
      resolvedName = parsedName
      break
    }
  }

  if (resolvedId <= 0 || resolvedName === '') return null
  return { id: resolvedId, name: resolvedName }
}

function mapPhoneTypes(items: unknown[]): ComboDataDTO[] {
  return items
    .map((item) => toComboDTO(item, ['id', 'type', 'phonenumbertype', 'phoneType'], ['name', 'description', 'typedesc']))
    .filter((item): item is ComboDataDTO => item !== null)
}

function mapDocumentTypes(items: unknown[]): ComboDataDTO[] {
  return items
    .map((item) => toComboDTO(item, ['documentType', 'id', 'value'], ['shortName', 'name', 'description']))
    .filter((item): item is ComboDataDTO => item !== null)
}

function parseCreatedHealthEntityId(rawData: unknown): number {
  const direct = parsePositiveNumber(rawData)
  if (direct != null) return direct

  const rawObj = toRecord(rawData)
  if (rawObj) {
    const candidates = [
      rawObj.medereentity,
      rawObj.medereEntity,
      rawObj.healthEntityId,
      rawObj.id,
      rawObj.value,
      rawObj.data,
    ]

    for (const candidate of candidates) {
      const parsed = parsePositiveNumber(candidate)
      if (parsed != null) return parsed
    }
  }

  throw new Error('No se pudo obtener el id de la obra social creada')
}

function filterAndPaginatePhoneTypes(items: ComboDataDTO[], filterValue = '', offSet = 0, pageSize = 50): ComboDataDTO[] {
  const normalizedFilter = (filterValue ?? '').trim().toLowerCase()
  const filtered = normalizedFilter.length > 0
    ? items.filter((item) => (item.name ?? '').toLowerCase().includes(normalizedFilter))
    : items

  const start = Math.max(offSet ?? 0, 0)
  const size = Math.max(pageSize ?? filtered.length, 0)
  return filtered.slice(start, start + size)
}

export function useHealthEntityService() {
  function getAllHealthEntities(
    offSet: number,
    pageSize: number,
    params: HealthEntityListQueryParams = {},
  ): Promise<HealthEntityListItemDTO[]> {
    const healthEntityStatus: boolean[] = params.healthEntityStatus ?? [true, false]
    const typeFilter: string[] = params.typeFilter ?? []
    const filters = params.filterDTO ?? params.cardFilterDTO ?? null

    return axiosInstance.post('/api/secured/healthentity/getAllHealthEntities', {
      loadConfigDTO: {
        offSet,
        pageSize,
        sortingFieldInfoList: params.multiSortMeta,
        filters,
      },
      healthEntityStatus,
      typeFilter,
    }).then((response) => response.data as HealthEntityListItemDTO[])
  }

  function getHealthEntityById(healthEntityId: number): Promise<HealthEntitySaveDTO> {
    return axiosInstance.get('/api/secured/healthentity/getHealthEntityById', {
      params: {
        healthEntityId,
      },
    }).then((response) => toFrontendPayload(response.data as HealthEntitySaveBackendDTO))
  }

  function saveHealthEntity(healthEntity: HealthEntitySaveDTO): Promise<number> {
    return axiosInstance.post('/api/secured/healthentity', toBackendPayload(healthEntity))
      .then((response) => parseCreatedHealthEntityId(response.data))
  }

  function updateHealthEntity(healthEntity: HealthEntitySaveDTO) {
    return axiosInstance.put('/api/secured/healthentity', toBackendPayload(healthEntity))
  }

  function deleteHealthEntity(healthEntityId: number): Promise<void> {
    return axiosInstance.delete('/api/secured/healthentity', {
      params: {
        healthEntityId,
      },
    }).then(() => undefined)
  }
  function getHealthEntityClasses({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getHealthEntityClasses', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getContractModalities({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getContractModalities', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getRecipeValidators({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getRecipeValidators', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => extractArray(response.data)
      .map((item) => toComboDTO(item, ['id', 'recipeValidator', 'value'], ['name', 'description', 'label']))
      .filter((item): item is ComboDataDTO => item !== null))
  }

  function getStudyOrderRepositories({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getStudyOrderRepositories', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => extractArray(response.data)
      .map((item) => toComboDTO(item, ['id', 'studyOrderRepository', 'value'], ['name', 'description', 'label']))
      .filter((item): item is ComboDataDTO => item !== null))
  }

  function getInformationProviders({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getInformationProviders', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => extractArray(response.data)
      .map((item) => toComboDTO(item, ['id', 'informationProvider', 'value'], ['name', 'description', 'label']))
      .filter((item): item is ComboDataDTO => item !== null))
  }

  function uploadHealthEntityRecipeLogo(file: File, oldFilename: string): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('oldFilename', oldFilename ?? '')
    return axiosInstance.post('/api/secured/profile/profiledataform/uploadLogoFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((response) => response.data as string)
  }

  function getHealthEntityRecipeLogo(logoName: string): Promise<Blob> {
    return axiosInstance.get('/api/secured/profile/profiledataform/findLogo', {
      params: { logoName },
      responseType: 'blob',
    }).then((response) => response.data as Blob)
  }

  function getHealthEntityVatCustomerCategories({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/supplier/getVatCustomerCategories', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => extractArray(response.data)
      .map((item) => toComboDTO(item, ['vatcustomercategory', 'id'], ['categoryname', 'name', 'description']))
      .filter((item): item is ComboDataDTO => item !== null))
  }

  function getHealthEntityDocumentTypes({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/profile/profiledataform/loadDocumentTypes', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => mapDocumentTypes(extractArray(response.data)))
  }

  function getHealthEntityAfipStatusByVatCustomerCategory(
    { filterValue, offSet, pageSize, params }: GetItemsProps<{ vatCustomerCategory?: number }>,
  ): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/supplier/getAfipStatusByVatCustomerCategory', {
      params: {
        inputtext: filterValue ?? '',
        offSet,
        pageSize,
        vatCustomerCategory: params?.vatCustomerCategory ?? -1,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getHealthEntityInvoiceIssuerEntities({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getInvoiceIssuerEntities', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getHealthEntityPOSByVatCategoryAndIssuer(
    { filterValue, offSet, pageSize, params }: GetItemsProps<{ catreceiver?: number, invoicingcompany?: number }>,
  ): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getPOSByVatCategoryAndIssuer', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
        catreceiver: params?.catreceiver ?? -1,
        invoicingcompany: params?.invoicingcompany ?? -1,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getHealthEntityPaymentMethods({ filterValue }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/supplier/getPaymentMethods', {
      params: {
        inputText: filterValue ?? '',
      },
    }).then((response) => extractArray(response.data)
      .map((item) => toComboDTO(item, ['paymentMethod', 'id'], ['methodName', 'name']))
      .filter((item): item is ComboDataDTO => item !== null))
  }

  function getHealthEntityBankAccountsByPaymentMethod(
    { filterValue, offSet, pageSize, params }: GetItemsProps<{ medereentity?: number, paymentMethod?: number }>,
  ): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/healthentity/getBankAccountsByPaymentMethod', {
      params: {
        inputText: filterValue ?? '',
        offSet,
        pageSize,
        medereentity: params?.medereentity ?? -1,
        paymentMethod: params?.paymentMethod ?? -1,
      },
    }).then((response) => normalizeComboItems(response.data))
  }

  function getHealthEntityPhoneTypes({ filterValue = '', offSet = 0, pageSize = 50 }: GetItemsProps): Promise<ComboDataDTO[]> {
    return axiosInstance.get('/api/secured/medereentity/getPhoneTypes', {
      params: {
        filter: filterValue,
        offSet,
        pageSize,
      },
    }).then((response) => {
      const mappedPhoneTypes = mapPhoneTypes(extractArray(response.data))
      if (mappedPhoneTypes.length > 0) {
        return mappedPhoneTypes
      }
      return filterAndPaginatePhoneTypes(fallbackPhoneTypes, filterValue, offSet, pageSize)
    }).catch(() => {
      return filterAndPaginatePhoneTypes(fallbackPhoneTypes, filterValue, offSet, pageSize)
    })
  }

  return {
    getAllHealthEntities,
    getHealthEntityById,
    saveHealthEntity,
    updateHealthEntity,
    deleteHealthEntity,
    getHealthEntityClasses,
    getContractModalities,
    getRecipeValidators,
    getStudyOrderRepositories,
    getInformationProviders,
    uploadHealthEntityRecipeLogo,
    getHealthEntityRecipeLogo,
    getHealthEntityVatCustomerCategories,
    getHealthEntityDocumentTypes,
    getHealthEntityAfipStatusByVatCustomerCategory,
    getHealthEntityInvoiceIssuerEntities,
    getHealthEntityPOSByVatCategoryAndIssuer,
    getHealthEntityPaymentMethods,
    getHealthEntityBankAccountsByPaymentMethod,
    getHealthEntityPhoneTypes,
  }
}
export function getHealthEntityCurrentAfipStep(healthEntityId: number): Promise<number> {
  return axiosInstance.get('/api/secured/healthentity/getCurrentAfipStep', {
    params: { healthEntityId },
  }).then((response) => response.data as number)
}

export function generateHealthEntityAfipPrivateKey(
  healthEntityId: number,
  afipAlias: string,
  vatID: string,
  afipproductionmode: boolean,
  iibbperceptionagent: boolean,
): Promise<boolean> {
  return axiosInstance.get('/api/secured/healthentity/generateAfipPrivateKey', {
    params: {
      healthEntityId,
      afipAlias,
      vatID,
      afipproductionmode,
      iibbperceptionagent,
    },
  }).then((response) => response.data as boolean)
    .catch((error) => {
      console.error('Error en generateHealthEntityAfipPrivateKey:', error?.response ?? error)
      return false
    })
}

export function generateHealthEntityCSRCertificate(healthEntityId: number): Promise<string | null> {
  return axiosInstance.get('/api/secured/healthentity/generateCSRCertificate', {
    params: { healthEntityId },
  }).then((response) => response.data as string)
    .catch((error) => {
      console.error('Error en generateHealthEntityCSRCertificate:', error?.response ?? error)
      return null
    })
}

export function saveHealthEntityAfipCertificate(
  healthEntityId: number,
  dueDate: Date,
  certificate: string,
): Promise<boolean> {
  return axiosInstance.get('/api/secured/healthentity/saveAfipCertificate', {
    params: {
      healthEntityId,
      dueDateUTC: dueDate?.getTime(),
      certificate: certificate.replace(/\n/g, '\r\n'),
    },
  }).then((response) => response.data as boolean)
    .catch((error) => {
      console.error('Error en saveHealthEntityAfipCertificate:', error?.response ?? error)
      return false
    })
}

export function testHealthEntityAfipConnection(
  healthEntityId: number,
  afipProductionMode: boolean,
): Promise<ResponseDTO> {
  return axiosInstance.post('/api/secured/healthentity/testAfipConnection', null, {
    params: {
      healthEntityId,
      afipProductionMode,
    },
  }).then((response) => response.data as ResponseDTO)
}

export function deleteHealthEntity(healthEntityId: number): Promise<void> {
  return axiosInstance.delete('/api/secured/healthentity', {
    params: {
      healthEntityId,
    },
  }).then(() => undefined)
}
